import { ref } from 'vue'
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  increment
} from 'firebase/firestore'
import { db } from '@/firebase/firebase'

export function useEvent() {
  const loading = ref(false)
  const error = ref(null)

  /**
   * 모든 이벤트 조회 (Admin용)
   */
  const getAllEvents = async () => {
    loading.value = true
    error.value = null

    try {
      const q = query(
        collection(db, 'events'),
        orderBy('createdAt', 'desc')
      )
      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
    } catch (err) {
      console.error('이벤트 조회 실패:', err)
      error.value = err.message
      return []
    } finally {
      loading.value = false
    }
  }

  /**
   * 활성 이벤트 조회 (사용자용, 페이지네이션)
   */
  const getActiveEvents = async (limitCount = 10, lastDoc = null) => {
    loading.value = true
    error.value = null

    try {
      let q
      const now = new Date().toISOString()

      if (lastDoc) {
        q = query(
          collection(db, 'events'),
          where('isActive', '==', true),
          orderBy('createdAt', 'desc'),
          startAfter(lastDoc),
          limit(limitCount)
        )
      } else {
        q = query(
          collection(db, 'events'),
          where('isActive', '==', true),
          orderBy('createdAt', 'desc'),
          limit(limitCount)
        )
      }

      const snapshot = await getDocs(q)
      const events = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))

      // 진행 중인 이벤트만 필터링 (endDate가 현재 이후이거나 없는 경우)
      const activeEvents = events.filter(event => {
        if (!event.endDate) return true
        return event.endDate >= now.split('T')[0]
      })

      return {
        events: activeEvents,
        lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
        hasMore: snapshot.docs.length === limitCount
      }
    } catch (err) {
      console.error('활성 이벤트 조회 실패:', err)
      error.value = err.message
      return { events: [], lastDoc: null, hasMore: false }
    } finally {
      loading.value = false
    }
  }

  /**
   * 단일 이벤트 조회
   */
  const getEventById = async (eventId) => {
    loading.value = true
    error.value = null

    try {
      const docRef = doc(db, 'events', eventId)
      const docSnap = await getDoc(docRef)

      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        }
      }
      return null
    } catch (err) {
      console.error('이벤트 조회 실패:', err)
      error.value = err.message
      return null
    } finally {
      loading.value = false
    }
  }

  /**
   * 이벤트 생성 (Admin)
   */
  const createEvent = async (eventData) => {
    loading.value = true
    error.value = null

    try {
      const now = new Date().toISOString()
      const docRef = await addDoc(collection(db, 'events'), {
        title: eventData.title,
        content: eventData.content,
        thumbnailUrl: eventData.thumbnailUrl || '',

        // 이벤트 기간
        startDate: eventData.startDate || now.split('T')[0],
        endDate: eventData.endDate || '',

        // 참여 방식: 'direct' (직접 참여) 또는 'link' (외부 링크)
        participationType: eventData.participationType || 'direct',
        externalLink: eventData.externalLink || '',

        // 상태
        isActive: true,

        // 통계
        viewCount: 0,
        participantCount: 0,

        // 타임스탬프
        createdAt: now,
        updatedAt: now
      })

      return docRef.id
    } catch (err) {
      console.error('이벤트 생성 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * 이벤트 수정 (Admin)
   */
  const updateEvent = async (eventId, eventData) => {
    loading.value = true
    error.value = null

    try {
      const docRef = doc(db, 'events', eventId)
      await updateDoc(docRef, {
        ...eventData,
        updatedAt: new Date().toISOString()
      })
    } catch (err) {
      console.error('이벤트 수정 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * 이벤트 활성화/비활성화 토글 (Admin)
   */
  const toggleEventActive = async (eventId, isActive) => {
    loading.value = true
    error.value = null

    try {
      const docRef = doc(db, 'events', eventId)
      await updateDoc(docRef, {
        isActive,
        updatedAt: new Date().toISOString()
      })
    } catch (err) {
      console.error('이벤트 상태 변경 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * 이벤트 삭제 (Admin)
   */
  const deleteEvent = async (eventId) => {
    loading.value = true
    error.value = null

    try {
      // 이벤트 참여 내역도 삭제
      const participantsQuery = query(
        collection(db, 'eventParticipants'),
        where('eventId', '==', eventId)
      )
      const participantsSnapshot = await getDocs(participantsQuery)

      const deletePromises = participantsSnapshot.docs.map(doc =>
        deleteDoc(doc.ref)
      )
      await Promise.all(deletePromises)

      // 이벤트 삭제
      await deleteDoc(doc(db, 'events', eventId))
    } catch (err) {
      console.error('이벤트 삭제 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * 조회수 증가
   */
  const incrementViewCount = async (eventId) => {
    try {
      const docRef = doc(db, 'events', eventId)
      await updateDoc(docRef, {
        viewCount: increment(1)
      })
    } catch (err) {
      console.error('조회수 증가 실패:', err)
    }
  }

  /**
   * 이벤트 참여하기 (직접 참여)
   */
  const participateEvent = async (eventId, participantData) => {
    loading.value = true
    error.value = null

    try {
      const now = new Date().toISOString()

      // 중복 참여 체크 (전화번호 기준)
      const existingQuery = query(
        collection(db, 'eventParticipants'),
        where('eventId', '==', eventId),
        where('phone', '==', participantData.phone)
      )
      const existingSnapshot = await getDocs(existingQuery)

      if (!existingSnapshot.empty) {
        throw new Error('이미 참여한 이벤트입니다.')
      }

      // 참여자 등록
      await addDoc(collection(db, 'eventParticipants'), {
        eventId,
        name: participantData.name,
        phone: participantData.phone,
        userId: participantData.userId || null,
        agreedAt: now,
        createdAt: now
      })

      // 이벤트 참여자 수 증가
      const eventRef = doc(db, 'events', eventId)
      await updateDoc(eventRef, {
        participantCount: increment(1)
      })

      return true
    } catch (err) {
      console.error('이벤트 참여 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * 이벤트 참여 여부 확인
   */
  const checkParticipation = async (eventId, phone) => {
    try {
      const q = query(
        collection(db, 'eventParticipants'),
        where('eventId', '==', eventId),
        where('phone', '==', phone)
      )
      const snapshot = await getDocs(q)
      return !snapshot.empty
    } catch (err) {
      console.error('참여 여부 확인 실패:', err)
      return false
    }
  }

  /**
   * 이벤트 참여자 목록 조회 (Admin)
   */
  const getEventParticipants = async (eventId) => {
    loading.value = true
    error.value = null

    try {
      const q = query(
        collection(db, 'eventParticipants'),
        where('eventId', '==', eventId),
        orderBy('createdAt', 'desc')
      )
      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
    } catch (err) {
      console.error('참여자 목록 조회 실패:', err)
      error.value = err.message
      return []
    } finally {
      loading.value = false
    }
  }

  /**
   * 진행 중인 이벤트 수 조회 (배지용)
   */
  const getActiveEventCount = async () => {
    try {
      const now = new Date().toISOString().split('T')[0]
      const q = query(
        collection(db, 'events'),
        where('isActive', '==', true)
      )
      const snapshot = await getDocs(q)

      // 진행 중인 이벤트만 카운트
      const activeCount = snapshot.docs.filter(doc => {
        const data = doc.data()
        if (!data.endDate) return true
        return data.endDate >= now
      }).length

      return activeCount
    } catch (err) {
      console.error('이벤트 수 조회 실패:', err)
      return 0
    }
  }

  return {
    loading,
    error,
    getAllEvents,
    getActiveEvents,
    getEventById,
    createEvent,
    updateEvent,
    toggleEventActive,
    deleteEvent,
    incrementViewCount,
    participateEvent,
    checkParticipation,
    getEventParticipants,
    getActiveEventCount
  }
}
